import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/lib/i18n';
import api from '@/lib/api';
import { Bell, LogOut, Globe } from 'lucide-react';

export function Header() {
  const { user, logout } = useAuth();
  const { t, lang, setLanguage } = useI18n();
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    if (!user?.tenant_id || user?.role === 'superadmin') {
      setBranding(null);
      return;
    }

    api.get(`/tenants/${user.tenant_id}/settings/branding`)
      .then((res) => setBranding(res.data?.branding || null))
      .catch(() => setBranding(null));
  }, [user?.tenant_id, user?.role]);

  const isTenantUser = user?.tenant_id && user?.role !== 'superadmin';
  const companyName = branding?.company_name || '';
  const logoUrl = branding?.logo_url || '';
  const fallbackInitial = (companyName || user?.name || '?').charAt(0);

  return (
    <header className="h-14 bg-[#09090b] border-b border-white/10 flex items-center justify-between px-6 flex-shrink-0" data-testid="app-header">
      <div className="flex items-center gap-3 min-w-0">
        {isTenantUser ? (
          <>
            <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
              {logoUrl ? (
                <img src={logoUrl} alt="" className="w-full h-full object-contain p-1" />
              ) : (
                <span className="text-xs font-bold text-[#EF694C]">{fallbackInitial}</span>
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm text-white font-semibold truncate" data-testid="tenant-header-name">
                {companyName || 'Mandant'}
              </p>
              <p className="text-[11px] text-[#a1a1aa] truncate">
                {t[user?.role] || user?.role}
              </p>
            </div>
          </>
        ) : (
          <span className="text-sm text-[#a1a1aa]">{t[user?.role] || user?.role}</span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => setLanguage(lang === 'de' ? 'en' : 'de')}
          data-testid="language-toggle"
          className="flex items-center gap-1.5 h-8 px-2.5 text-[#a1a1aa] hover:text-white hover:bg-white/5 rounded-lg transition-all text-xs font-medium"
          title={lang === 'de' ? 'Switch to English' : 'Auf Deutsch wechseln'}
        >
          <Globe className="w-3.5 h-3.5" />
          <span>{lang === 'de' ? 'DE' : 'EN'}</span>
        </button>

        <div className="w-px h-5 bg-white/10" />

        <div className="flex items-center gap-2 px-2">
          <div className="w-7 h-7 rounded-full bg-[#EF694C]/10 flex items-center justify-center">
            <span className="text-xs font-bold text-[#EF694C]">{user?.name?.charAt(0) || '?'}</span>
          </div>
          <span className="text-sm text-white font-medium hidden md:inline">{user?.name}</span>
        </div>

        <button
          onClick={logout}
          data-testid="logout-btn"
          className="flex items-center gap-1.5 h-8 px-2.5 text-[#a1a1aa] hover:text-[#F43F5E] hover:bg-[#F43F5E]/10 rounded-lg transition-all text-xs font-medium"
        >
          <LogOut className="w-3.5 h-3.5" />
          <span className="hidden md:inline">{t.signOut}</span>
        </button>
      </div>
    </header>
  );
}
